/**
 * =============================================================================
 *  BO TACH TU CUA CONTENT STREAM
 * =============================================================================
 *
 *  pdf-lib KHONG doc content stream. No doc cay object (dictionary, mang, luong)
 *  va no ghi lai duoc cay do, nhung phan THAN cua mot luong noi dung - chuoi
 *  toan tu ve trang - voi no chi la mot day byte mo duc.
 *
 *  Nen de di chuyen hay xoa mot anh da nhung, ta phai tu doc day byte do. Tep
 *  nay lam DUNG mot viec: cat day byte thanh tung tu, kem VI TRI BYTE cua tung
 *  tu. Vi tri byte moi la thu quan trong: moi phep sua sau nay deu la cat va noi
 *  tren chinh mang byte goc, khong phai dung lai mot content stream moi tu cac
 *  tu da doc.
 *
 *  Vi sao cat-va-noi chu khong dung lai: dung lai nghia la ta phai tai tao dung
 *  moi thu ta khong hieu - chuoi nhi phan trong toan tu Tj, anh noi dong BI/ID/EI,
 *  cach viet so cua tung bo sinh PDF. Mot cho tai tao sai la mot trang trang.
 *  Cat-va-noi thi moi byte ta khong dong den van con nguyen ven tuyet doi.
 *
 * =============================================================================
 *  ANH NOI DONG (BI ... ID ... EI) - CAI BAY LON NHAT
 * =============================================================================
 *  Giua ID va EI la DU LIEU ANH THO, khong phai toan tu. Trong do co the co dung
 *  byte cua chu "EI", co dau ngoac don le loi, co bat cu thu gi. Mot bo tach tu
 *  khong biet den BI/ID/EI se lac duong ngay tai do va moi vi tri byte sau no
 *  deu sai - tuc la mot phep cat vao giua mot toan tu.
 *
 *  O day ta xu ly no bang cach: gap `ID` thi nhay thang toi `EI` dung sau mot ky
 *  tu trang, va danh dau ca doan la MOT tu kieu 'inline-image'. Ta khong doc noi
 *  dung anh do - ta chi can biet no NAM O DAU de khong cat vao giua no.
 */

export type TokenKind =
  /** So: 12, -3.5, .5 */
  | 'number'
  /** Ten: /Im0 */
  | 'name'
  /** Chuoi trong ngoac don: (abc) */
  | 'string'
  /** Chuoi thap luc: <414243> */
  | 'hex-string'
  /** Dau mo/dong mang hoac tu dien: [ ] << >> */
  | 'array-open'
  | 'array-close'
  | 'dict-open'
  | 'dict-close'
  /** Toan tu: q, Q, cm, Do, BT... */
  | 'operator'
  /** Ca khoi BI...ID...EI cua anh noi dong. Khong duoc cat vao trong. */
  | 'inline-image'
  /** Chu thich % ... den cuoi dong. */
  | 'comment';

export interface Token {
  kind: TokenKind;
  /** Vi tri byte bat dau, tinh trong mang duoc tach. */
  start: number;
  /** Vi tri byte NGAY SAU tu. bytes.slice(start, end) la nguyen van tu do. */
  end: number;
  /**
   * Dang van ban cua tu, chi cho 'operator', 'name' va 'number'.
   *
   * Cac loai con lai KHONG co: chuoi PDF la chuoi BYTE, khong phai chuoi
   * Unicode, va giai ma no ra chuoi JavaScript la mot phep bien doi mat mat ma
   * khong cho nao trong tep nay can den.
   */
  text?: string;
  /** Gia tri so, chi cho 'number'. */
  value?: number;
}

const SPACE = new Set([0x00, 0x09, 0x0a, 0x0c, 0x0d, 0x20]);
const DELIM = new Set([0x28, 0x29, 0x3c, 0x3e, 0x5b, 0x5d, 0x7b, 0x7d, 0x2f, 0x25]);

function isSpace(b: number): boolean {
  return SPACE.has(b);
}
function isDelim(b: number): boolean {
  return DELIM.has(b);
}
function isRegular(b: number): boolean {
  return !isSpace(b) && !isDelim(b);
}

/** Doc mot doan byte thanh chuoi ASCII. Byte > 127 duoc giu nguyen ma. */
function ascii(bytes: Uint8Array, start: number, end: number): string {
  let out = '';
  for (let i = start; i < end; i++) out += String.fromCharCode(bytes[i] as number);
  return out;
}

/**
 * Cat mot content stream thanh cac tu.
 *
 * Khong bao gio nem loi. Byte rac cho ra mot tu 'operator' vo nghia, va tang
 * tren se tu choi vi khong nhan ra no - tot hon la lam do ca duong sua.
 */
export function tokenize(bytes: Uint8Array): Token[] {
  const out: Token[] = [];
  const n = bytes.length;
  let i = 0;

  while (i < n) {
    const b = bytes[i] as number;

    if (isSpace(b)) {
      i++;
      continue;
    }

    // --- Chu thich ---
    if (b === 0x25) {
      const start = i;
      while (i < n && bytes[i] !== 0x0a && bytes[i] !== 0x0d) i++;
      out.push({ kind: 'comment', start, end: i });
      continue;
    }

    // --- Chuoi trong ngoac don: dem do sau, ton trong dau \ ---
    if (b === 0x28) {
      const start = i;
      i++;
      let depth = 1;
      while (i < n && depth > 0) {
        const c = bytes[i] as number;
        if (c === 0x5c) {
          i += 2; // \( \) \\ ... - bo qua ca ky tu sau dau cheo nguoc
          continue;
        }
        if (c === 0x28) depth++;
        else if (c === 0x29) depth--;
        i++;
      }
      out.push({ kind: 'string', start, end: i });
      continue;
    }

    // --- << >> hoac <hex> ---
    if (b === 0x3c) {
      if (bytes[i + 1] === 0x3c) {
        out.push({ kind: 'dict-open', start: i, end: i + 2 });
        i += 2;
        continue;
      }
      const start = i;
      i++;
      while (i < n && bytes[i] !== 0x3e) i++;
      i = Math.min(n, i + 1);
      out.push({ kind: 'hex-string', start, end: i });
      continue;
    }
    if (b === 0x3e && bytes[i + 1] === 0x3e) {
      out.push({ kind: 'dict-close', start: i, end: i + 2 });
      i += 2;
      continue;
    }

    if (b === 0x5b) {
      out.push({ kind: 'array-open', start: i, end: i + 1 });
      i++;
      continue;
    }
    if (b === 0x5d) {
      out.push({ kind: 'array-close', start: i, end: i + 1 });
      i++;
      continue;
    }

    // --- Ten: /Im0 ---
    if (b === 0x2f) {
      const start = i;
      i++;
      while (i < n && isRegular(bytes[i] as number)) i++;
      out.push({ kind: 'name', start, end: i, text: decodeName(bytes, start + 1, i) });
      continue;
    }

    // Dau ngoac nhon { } chi xuat hien trong ham PostScript cua tu dien to bong.
    // Giu nguyen lam toan tu de khong lac vi tri byte.
    if (b === 0x7b || b === 0x7d) {
      out.push({ kind: 'operator', start: i, end: i + 1, text: String.fromCharCode(b) });
      i++;
      continue;
    }

    // --- So hoac toan tu ---
    const start = i;
    while (i < n && isRegular(bytes[i] as number)) i++;
    if (i === start) {
      // Byte phan cach le loi khong roi vao nhanh nao o tren. Nuot mot byte de
      // vong lap khong bao gio dung yen.
      i++;
      continue;
    }
    const text = ascii(bytes, start, i);

    if (/^[+-]?(\d+\.?\d*|\.\d+)$/.test(text)) {
      out.push({ kind: 'number', start, end: i, text, value: Number(text) });
      continue;
    }

    // --- Anh noi dong: tu ID tro di la DU LIEU, khong phai toan tu ---
    if (text === 'BI') {
      const end = skipInlineImage(bytes, i);
      out.push({ kind: 'inline-image', start, end });
      i = end;
      continue;
    }

    out.push({ kind: 'operator', start, end: i, text });
  }

  return out;
}

/** Ten PDF cho phep #xx. Giai ma de so sanh voi ten trong /XObject. */
function decodeName(bytes: Uint8Array, start: number, end: number): string {
  let out = '';
  for (let i = start; i < end; i++) {
    const c = bytes[i] as number;
    if (c === 0x23 && i + 2 < end) {
      const hex = ascii(bytes, i + 1, i + 3);
      const v = Number.parseInt(hex, 16);
      if (Number.isFinite(v)) {
        out += String.fromCharCode(v);
        i += 2;
        continue;
      }
    }
    out += String.fromCharCode(c);
  }
  return out;
}

/**
 * Tu sau 'BI' den het 'EI'.
 *
 * Tim 'ID' truoc (ket thuc tu dien anh), roi tu do tim day byte "EI" co ky tu
 * trang ngay truoc va ky tu trang / het luong ngay sau. Do la phep do duy nhat
 * dac ta cho phep, va no van co the nham tren du lieu anh khong nen - nhung mot
 * lan nham chi lam ta TU CHOI sua, khong lam hong tep.
 */
function skipInlineImage(bytes: Uint8Array, from: number): number {
  const n = bytes.length;
  let i = from;
  // Tim toan tu ID
  while (i < n - 1) {
    if (bytes[i] === 0x49 && bytes[i + 1] === 0x44) {
      const before = i === 0 ? 0x20 : (bytes[i - 1] as number);
      if (isSpace(before) || isDelim(before)) {
        i += 2;
        break;
      }
    }
    i++;
  }
  // Mot ky tu trang duy nhat sau ID thuoc ve toan tu, phan con lai la du lieu.
  if (i < n && isSpace(bytes[i] as number)) i++;

  while (i < n - 1) {
    if (bytes[i] === 0x45 && bytes[i + 1] === 0x49) {
      const before = i === 0 ? 0x20 : (bytes[i - 1] as number);
      const after = i + 2 >= n ? 0x20 : (bytes[i + 2] as number);
      if (isSpace(before) && (isSpace(after) || isDelim(after))) return i + 2;
    }
    i++;
  }
  return n;
}

/** Chi cac tu MANG NGHIA. Chu thich khong tham gia vao cu phap toan tu. */
export function meaningful(tokens: readonly Token[]): Token[] {
  return tokens.filter((t) => t.kind !== 'comment');
}
